import { useState, useEffect, useRef, FormEvent, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Lock, XCircle, Loader2, Check, Monitor } from 'lucide-react';
import { Link } from 'react-router-dom';
import { login, rfidLogin, ApiException } from '../api/client';

// DEBUG: RFID-Logs (auf false fuer Production)
const RFID_DEBUG = false;
function rfidLog(msg: string, data?: Record<string, unknown>) {
  if (RFID_DEBUG) {
    console.info(`[rfid] ${msg}`, data || '');
  }
}

/**
 * Login Page - Legacy Parity (PR-2)
 *
 * SEC-006: Stationsabhängige Login-Darstellung
 * - Unregistrierte Station: Nur neutrales Admin-Login
 * - Registrierte Station: Tabs (Chip/Admin) mit RFID als Default
 *
 * Gaps geschlossen: 1, 2, 3, 4, 5
 */

type LoginMode = 'rfid' | 'password';
type RfidState = 'ready' | 'scanning' | 'success' | 'error';

// Security: Nur interne Pfade erlauben (kein Open Redirect)
function getSafeReturnTo(returnTo: string | null): string {
  if (!returnTo) return '/dashboard';
  // Muss mit / beginnen und darf nicht mit // oder http beginnen
  if (!returnTo.startsWith('/') || returnTo.startsWith('//') || returnTo.toLowerCase().startsWith('/http')) {
    return '/dashboard';
  }
  return returnTo;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // returnTo aus URL Query Parameter
  const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
  
  // SEC-006: Station-Status
  const [stationStatus, setStationStatus] = useState<'loading' | 'registered' | 'unregistered'>('loading');
  
  // Login mode (nur relevant wenn registriert)
  const [mode, setMode] = useState<LoginMode>('password');
  
  // Password login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // RFID state
  const [rfidState, setRfidState] = useState<RfidState>('ready');
  const [chipBuffer, setChipBuffer] = useState('');
  const emailInputRef = useRef<HTMLInputElement>(null);
  const rfidInputRef = useRef<HTMLInputElement>(null);
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refocusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Inaktivitäts-Timer Konstanten (Kiosk-Standard)
  const SCAN_COMPLETE_DELAY_MS = 200; // Nach 200ms ohne Eingabe gilt Scan als komplett
  const MIN_CHIP_LENGTH = 8;

  // Station-Check beim Laden (SEC-006)
  useEffect(() => {
    const checkStation = async () => {
      try {
        const res = await fetch('/api/station/status', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.id) {
            // Station ist gekoppelt
            setStationStatus('registered');
            setMode('rfid'); // RFID als Default auf registrierten Stationen
          } else {
            setStationStatus('unregistered');
            setMode('password');
          }
        } else if (res.status === 404) {
          // Keine Station = unregistriert
          setStationStatus('unregistered');
        } else {
          setStationStatus('unregistered');
        }
      } catch {
        setStationStatus('unregistered');
      }
    };
    checkStation();
  }, []);

  // Auto-Focus auf E-Mail-Feld bei Password-Mode
  useEffect(() => {
    if (mode === 'password' && stationStatus !== 'loading' && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [mode, stationStatus]);

  // RFID Login Handler
  const doRfidLogin = useCallback(async (chipId: string, trigger: 'ENTER' | 'TIMEOUT') => {
    rfidLog('doRfidLogin called', { chipId: chipId.substring(0, 4) + '...', length: chipId.length, trigger });
    
    if (loading) {
      rfidLog('doRfidLogin aborted: already loading');
      return;
    }
    
    const normalized = chipId.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
    rfidLog('normalized chipId', { normalized: normalized.substring(0, 4) + '...', length: normalized.length });
    
    if (normalized.length < MIN_CHIP_LENGTH) {
      rfidLog('doRfidLogin aborted: too short', { length: normalized.length, min: MIN_CHIP_LENGTH });
      setChipBuffer('');
      setRfidState('ready');
      return;
    }
    
    setLoading(true);
    setRfidState('scanning');
    setError('');
    
    try {
      rfidLog('calling POST /api/station/rfid', { chipId: normalized.substring(0, 4) + '...' });
      const result = await rfidLogin(normalized);
      rfidLog('rfidLogin SUCCESS', { redirectUrl: result.redirectUrl, expiresIn: result.expiresIn });
      
      // Erfolg: Kurz grün zeigen, dann redirect
      setRfidState('success');
      setChipBuffer('');
      setTimeout(() => {
        navigate(result.redirectUrl || '/station');
      }, 500);
    } catch (err) {
      rfidLog('rfidLogin ERROR', { err });
      if (err instanceof ApiException) {
        setError(err.data.error || 'Chip nicht erkannt');
        rfidLog('ApiException', { code: err.data.code, error: err.data.error });
      } else {
        setError('Verbindungsfehler');
      }
      setRfidState('error');
      setChipBuffer('');
      // Nach 2.5s zurück zu ready
      setTimeout(() => {
        setRfidState('ready');
        setError('');
        // Refocus nach Error
        rfidInputRef.current?.focus();
      }, 2500);
    } finally {
      setLoading(false);
    }
  }, [loading, navigate]);

  // Hidden Input Handler für RFID (robuster als globaler keypress)
  const handleRfidInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    rfidLog('onChange', { newValue, length: newValue.length, activeElement: document.activeElement?.tagName });
    
    // Während success/error: Eingaben ignorieren
    if (rfidState === 'success' || rfidState === 'error') {
      rfidLog('onChange ignored: rfidState', { rfidState });
      return;
    }
    
    setChipBuffer(newValue);
    
    // Visuelles Feedback: Nach erstem Zeichen scanning
    if (newValue.length > 0 && rfidState === 'ready') {
      setRfidState('scanning');
    }
    
    // Inaktivitäts-Timer: Nach SCAN_COMPLETE_DELAY_MS ohne Eingabe gilt Scan als komplett
    if (scanTimeoutRef.current) {
      clearTimeout(scanTimeoutRef.current);
    }
    
    if (newValue.length >= MIN_CHIP_LENGTH) {
      scanTimeoutRef.current = setTimeout(() => {
        rfidLog('scan complete via TIMEOUT', { buffer: newValue });
        doRfidLogin(newValue, 'TIMEOUT');
      }, SCAN_COMPLETE_DELAY_MS);
    }
  }, [rfidState, doRfidLogin]);

  const handleRfidKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    rfidLog('onKeyDown', { key: e.key, bufferLength: chipBuffer.length });
    
    if (e.key === 'Enter') {
      e.preventDefault();
      // Enter-Trigger: Sofort absenden
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
      rfidLog('scan complete via ENTER', { buffer: chipBuffer });
      doRfidLogin(chipBuffer, 'ENTER');
    }
  }, [chipBuffer, doRfidLogin]);

  // Fokus-Robustheit: Auto-Focus und Refocus bei Blur (Kiosk-Standard)
  useEffect(() => {
    if (mode !== 'rfid' || stationStatus !== 'registered') {
      return;
    }
    
    rfidLog('Chip-Tab mounted', { mode, stationStatus, rfidState });
    
    // Initial Focus
    const focusInput = () => {
      if (rfidInputRef.current && document.activeElement !== rfidInputRef.current) {
        rfidInputRef.current.focus();
        rfidLog('focus set on rfidInput');
      }
    };
    
    focusInput();
    
    // Refocus bei Blur (nach kurzer Verzögerung)
    const handleBlur = () => {
      if (rfidState === 'ready' || rfidState === 'scanning') {
        refocusTimeoutRef.current = setTimeout(focusInput, 100);
      }
    };
    
    const input = rfidInputRef.current;
    input?.addEventListener('blur', handleBlur);
    
    // Cleanup
    return () => {
      input?.removeEventListener('blur', handleBlur);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
      if (refocusTimeoutRef.current) clearTimeout(refocusTimeoutRef.current);
    };
  }, [mode, stationStatus, rfidState]);

  // Klick auf Card fokussiert hidden input
  const handleCardClick = useCallback(() => {
    rfidLog('card clicked, focusing input');
    rfidInputRef.current?.focus();
  }, []);

  // Password Login Handler
  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.requiresTwoFactor) {
        // returnTo an 2FA-Seite weitergeben
        navigate(`/2fa/verify?returnTo=${encodeURIComponent(returnTo)}`);
      } else if (result.requiresTwoFactorSetup) {
        // returnTo an 2FA-Setup weitergeben
        navigate(`/2fa/setup?returnTo=${encodeURIComponent(returnTo)}`);
      } else {
        // Direkt zu returnTo navigieren
        navigate(returnTo);
      }
    } catch (err) {
      if (err instanceof ApiException) {
        if (err.data.code === 'RATE_LIMITED') {
          setError(`Zu viele Versuche. Bitte warten Sie ${err.data.retryAfter} Sekunden.`);
        } else {
          setError(err.data.error || 'Anmeldung fehlgeschlagen');
        }
      } else {
        setError('Verbindungsfehler. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Loading-State
  if (stationStatus === 'loading') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
          <div className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          </div>
        </div>
      </div>
    );
  }

  // UNREGISTRIERT: Nur neutrales Admin-Login (SEC-006 - keine RFID-Hinweise)
  if (stationStatus === 'unregistered') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-slate-900">Anmeldung</h1>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-center">
              {error}
            </div>
          )}

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                ref={emailInputRef}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Passwort
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-2 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-medium transition-colors disabled:opacity-50"
            >
              {loading ? 'Wird geprüft...' : 'Anmelden'}
            </button>

            <div className="text-center mt-3">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                Passwort vergessen?
              </Link>
            </div>
          </form>

          {/* Station koppeln Link */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <Link
              to="/pair"
              className="flex items-center justify-center gap-2 w-full py-2 px-4 border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <Monitor className="w-4 h-4" />
              Station koppeln
            </Link>
            <p className="text-xs text-slate-500 text-center mt-2">
              Dieses Gerät als Station registrieren
            </p>
          </div>
        </div>
      </div>
    );
  }

  // REGISTRIERT: Voller Login-Flow mit Tabs (Chip + Admin)
  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-slate-900">wash-ops</h1>
          <p className="text-slate-500 mt-1">Anmeldung</p>
        </div>

        {/* Mode Tabs: Chip + Admin (Gap 3) */}
        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => setMode('rfid')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors flex items-center justify-center gap-2 ${
              mode === 'rfid'
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            Chip
          </button>
          <button
            type="button"
            onClick={() => setMode('password')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors flex items-center justify-center gap-2 ${
              mode === 'password'
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Lock className="w-4 h-4" />
            Admin
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-center">
            {error}
          </div>
        )}

        {/* RFID Mode (Gap 1, 4) */}
        {mode === 'rfid' && (
          <div className="text-center py-8 cursor-pointer" onClick={handleCardClick}>
            {/* Hidden Input fuer RFID Reader (Kiosk-Standard) */}
            <input
              ref={rfidInputRef}
              type="text"
              value={chipBuffer}
              onChange={handleRfidInputChange}
              onKeyDown={handleRfidKeyDown}
              className="sr-only"
              autoFocus
              aria-label="RFID Chip-ID"
              disabled={rfidState === 'success' || rfidState === 'error'}
            />

            {/* State: ready - Pulsierender Ring */}
            {rfidState === 'ready' && (
              <>
                <div className="relative inline-block">
                  <div className="absolute inset-0 rounded-full bg-blue-400/30 animate-ping" />
                  <div className="relative w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                    <CreditCard className="w-10 h-10 text-blue-600" />
                  </div>
                </div>
                <p className="text-xl mt-6 font-medium text-slate-900">Chip an Leser halten</p>
                <p className="text-slate-500 mt-2">Wird automatisch erkannt</p>
              </>
            )}

            {/* State: scanning */}
            {rfidState === 'scanning' && (
              <>
                <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto">
                  <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                </div>
                <p className="text-xl mt-6 font-medium text-slate-900">Wird geprüft...</p>
              </>
            )}

            {/* State: success */}
            {rfidState === 'success' && (
              <>
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <p className="text-xl mt-6 font-medium text-green-700">Willkommen!</p>
              </>
            )}

            {/* State: error */}
            {rfidState === 'error' && (
              <>
                <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                  <XCircle className="w-10 h-10 text-red-600" />
                </div>
                <p className="text-xl mt-6 font-medium text-red-700">Chip nicht erkannt</p>
                <p className="text-slate-500 mt-2">Bitte erneut versuchen</p>
              </>
            )}
          </div>
        )}

        {/* Password Mode */}
        {mode === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                ref={emailInputRef}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
                Passwort
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !email || !password}
              className="w-full py-2 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-md font-medium transition-colors disabled:opacity-50"
            >
              {loading ? 'Wird geprüft...' : 'Anmelden'}
            </button>

            <div className="text-center mt-3">
              <Link
                to="/forgot-password"
                className="text-sm text-slate-500 hover:text-slate-700 transition-colors"
              >
                Passwort vergessen?
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
